// src/components/facilityEvents/EventFormDialog.jsx
//
// Create or edit one of the facility's events (ShowFacility.jsx), in a dialog
// so the form and the list of events (EventList.jsx) no longer mix on the page.
// Same form as before: type, name, message, Aujourd'hui / Demain, Midi / Soir /
// Toute la journée, exact times, and « Ce que verra votre client ».
//
// Props: open, mode ('create' | 'edit'), initial (values to start from, or null
// for a new event), facilityName, facilityType, eventTypes (with labels),
// onSubmit({ name, type, date, end_date, message }) - a promise; a rejection's
// message is shown in the dialog - and onClose.
import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Grid, Typography, ToggleButton, ToggleButtonGroup,
  TextField, Stack, Button, Alert, Paper, Chip,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { toLocalInput } from './eventStatus';

const at = (dayOffset, hour, minute = 0) => {
  const d = new Date();
  d.setDate(d.getDate() + dayOffset);
  d.setHours(hour, minute, 0, 0);
  return toLocalInput(d);
};

/** A new event: dish of the day, today 11:00-23:00 for a restaurant (as before). */
export function defaultEventValues(facilityType) {
  const restaurant = facilityType === 'restaurant';
  return {
    name: '',
    type: 3,
    date: restaurant ? at(0, 11) : '',
    end_date: restaurant ? at(0, 23) : '',
    message: '',
  };
}

export default function EventFormDialog({
  open, mode = 'create', initial, facilityName, facilityType, eventTypes, onSubmit, onClose,
}) {
  const { t, i18n } = useTranslation();
  const [form, setForm] = useState(() => defaultEventValues(facilityType));
  const [quickDay, setQuickDay] = useState(0);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  // Each opening starts from `initial` (edit, « Refaire aujourd'hui ») or a blank event.
  useEffect(() => {
    if (!open) return;
    setForm(initial ? { ...defaultEventValues(facilityType), ...initial } : defaultEventValues(facilityType));
    setQuickDay(0);
    setError('');
    setSaving(false);
  }, [open, initial, facilityType]);

  const set = (patch) => { setError(''); setForm((s) => ({ ...s, ...patch })); };

  // Quick-fill helpers: an owner never has to type a date by hand.
  const shiftDay = (offset) => {
    setQuickDay(offset);
    setForm((s) => {
      const shift = (str, fallbackHour) => {
        const base = str ? new Date(str) : null;
        return at(offset, base ? base.getHours() : fallbackHour, base ? base.getMinutes() : 0);
      };
      return { ...s, date: shift(s.date, 11), end_date: shift(s.end_date, 15) };
    });
  };
  const applySlot = (startHour, endHour, endMinute = 0) =>
    set({ date: at(quickDay, startHour), end_date: at(quickDay, endHour, endMinute) });

  const submit = async () => {
    const name = (form.name || '').trim();
    const message = (form.message || '').trim();
    if (!name && !message) return setError(t('showFacility.eventValidation.needTitleAndMessage'));
    if (!name) return setError(t('showFacility.eventValidation.needTitle'));
    if (!message) return setError(t('showFacility.eventValidation.needMessage'));

    // Missing dates: today 11:00-23:00 for a restaurant, else now → in an hour (as before).
    let { date, end_date: endDate } = form;
    if (!date || !endDate) {
      const restaurant = facilityType === 'restaurant';
      const now = new Date();
      if (!date) date = restaurant ? at(0, 11) : toLocalInput(now);
      if (!endDate) endDate = restaurant ? at(0, 23) : toLocalInput(new Date(now.getTime() + 3600_000));
    }

    setSaving(true);
    try {
      await onSubmit({ name, type: Number(form.type), date, end_date: endDate, message });
    } catch (e) {
      setError(e?.message || t(mode === 'edit' ? 'showFacility.updateError' : 'showFacility.createEventError'));
    } finally {
      setSaving(false);
    }
  };

  const typeLabel = eventTypes.find((et) => et.value === Number(form.type))?.label;
  const caption = (key) => (
    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>{t(key)}</Typography>
  );

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle>{t(mode === 'edit' ? 'showFacility.editEventTitle' : 'showFacility.addEventTitle')}</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{t('showFacility.eventsIntro')}</Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} md={7}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                {caption('showFacility.eventTypeLabel')}
                <ToggleButtonGroup
                  value={Number(form.type)}
                  exclusive
                  onChange={(e, val) => { if (val !== null) set({ type: val }); }}
                  sx={{ flexWrap: 'wrap', gap: 1 }}
                >
                  {eventTypes.map((opt) => (
                    <ToggleButton key={opt.value} value={opt.value}
                      sx={{ textTransform: 'none', borderRadius: '4px !important', border: '1px solid rgba(0,0,0,0.23) !important' }}>
                      {opt.label}
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>
              </Grid>
              <Grid item xs={12}>
                <TextField label={t('showFacility.eventName')} fullWidth value={form.name}
                  onChange={(e) => set({ name: e.target.value })} />
              </Grid>
              <Grid item xs={12}>
                <TextField label={t('showFacility.message')} fullWidth inputProps={{ maxLength: 100 }} value={form.message}
                  onChange={(e) => set({ message: e.target.value })} />
              </Grid>
              <Grid item xs={12}>
                {caption('showFacility.quickDayLabel')}
                <Stack direction="row" spacing={1}>
                  <Button variant={quickDay === 0 ? 'contained' : 'outlined'} size="small" onClick={() => shiftDay(0)}>
                    {t('showFacility.today')}
                  </Button>
                  <Button variant={quickDay === 1 ? 'contained' : 'outlined'} size="small" onClick={() => shiftDay(1)}>
                    {t('showFacility.tomorrow')}
                  </Button>
                </Stack>
              </Grid>
              <Grid item xs={12}>
                {caption('showFacility.quickTimeLabel')}
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  <Button variant="outlined" size="small" onClick={() => applySlot(11, 15)}>{t('showFacility.slotLunch')}</Button>
                  <Button variant="outlined" size="small" onClick={() => applySlot(18, 23)}>{t('showFacility.slotEvening')}</Button>
                  <Button variant="outlined" size="small" onClick={() => applySlot(0, 23, 59)}>{t('showFacility.slotAllDay')}</Button>
                </Stack>
              </Grid>
              <Grid item xs={12}>
                {caption('showFacility.exactTimeLabel')}
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField label={t('showFacility.start')} type="datetime-local" fullWidth size="small"
                      InputLabelProps={{ shrink: true }} value={form.date} onChange={(e) => set({ date: e.target.value })} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField label={t('showFacility.end')} type="datetime-local" fullWidth size="small"
                      InputLabelProps={{ shrink: true }} value={form.end_date} onChange={(e) => set({ end_date: e.target.value })} />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Grid>

          {/* Live preview: exactly what the customer sees after scanning. */}
          <Grid item xs={12} md={5}>
            {caption('showFacility.previewLabel')}
            <Paper variant="outlined" sx={{ p: 2, bgcolor: '#fafafa' }}>
              <Typography variant="overline" color="text.secondary" sx={{ display: 'block' }}>{facilityName}</Typography>
              <Chip size="small" color="primary" variant="outlined" label={typeLabel} sx={{ my: 1 }} />
              <Typography variant="h6" sx={{ mb: 0.5 }}>{form.name || t('showFacility.previewTitlePlaceholder')}</Typography>
              <Typography sx={{ mb: 1, color: form.message ? 'text.primary' : 'text.disabled' }}>
                {form.message || t('showFacility.previewMessagePlaceholder')}
              </Typography>
              {form.date && form.end_date && (
                <Typography variant="body2" color="text.secondary">
                  {new Date(form.date).toLocaleString(i18n.language)} → {new Date(form.end_date).toLocaleString(i18n.language)}
                </Typography>
              )}
            </Paper>
          </Grid>
        </Grid>
        {error && <Alert severity="warning" sx={{ mt: 2 }} onClose={() => setError('')}>{error}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>{t('showFacility.cancel')}</Button>
        <Button variant="contained" onClick={submit} disabled={saving}>
          {t(mode === 'edit' ? 'showFacility.save' : 'showFacility.create')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
